/* What each server event refetches. The socket (`socket.ts`) says *that*
   something of the signed-in user's, or their org's, changed; this map turns that
   into TanStack Query invalidations, so every screen showing it refetches over
   REST. Events carry ids only — the data itself always comes from the API.

   A new event in backend/events/events.py: `sg events sync` regenerates
   `events.gen.ts`, and this file stops compiling until it has a line in EVENT_MAP. */
import { useQueryClient, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { appsKeys } from "./apps";
import { modelKeys } from "./models";
import { notificationKeys } from "./notifications";
import { overviewKeys } from "./overview";
import { connectSocket, type SocketMessage } from "./socket";
import { spaceKeys } from "./spaces";
import { taskKeys } from "./tasks";
import { threadKeys } from "./threads";
import { traceKeys } from "./traces";

import type { ServerEvent } from "./events.gen";

type EventType = ServerEvent["type"];
type DataOf<T extends EventType> = Extract<ServerEvent, { type: T }>["data"];

export const EVENT_MAP: { [T in EventType]: (data: DataOf<T>) => QueryKey[] } = {
  // every run is announced to the whole org: the Overview counts them all, and a task's
  // row shows how its run is going
  "run.started": (d) => [
    threadKeys.list(d.agent_id),
    threadKeys.runs(d.thread_id),
    taskKeys.all,
    traceKeys.runs(d.agent_id),
    overviewKeys.all,
  ],
  "run.ended": (d) => [
    threadKeys.list(d.agent_id),
    threadKeys.one(d.thread_id),
    threadKeys.messages(d.thread_id),
    threadKeys.runs(d.thread_id),
    taskKeys.all,
    traceKeys.runs(d.agent_id),
    traceKeys.one(d.run_id),
    overviewKeys.all,
  ],
  "provider_connection.changed": () => [modelKeys.all],
  "app_connection.changed": () => [appsKeys.all],
  "notification.created": () => [notificationKeys.inbox],
  // the consumer app's calls (consumer/src/features/calls): the call as its person reads it
  "call.changed": (d) => [["calls", d.call_id]],
  // an agent's tables: rows its runs, Builder or another tab wrote, or its columns changed
  "space_rows.changed": (d) => [spaceKeys.rows(d.agent_id), spaceKeys.tables(d.agent_id)],
  "space.changed": (d) => [spaceKeys.agent(d.agent_id)],
};

const listeners = new Set<(event: ServerEvent) => void>();

export function handleMessage(message: SocketMessage, queryClient: QueryClient): void {
  if (message.type === "resync") {
    queryClient.invalidateQueries(); // away too long to know what changed: refetch what's on screen
    return;
  }
  if (message.type === "error") {
    console.warn("socket: the server refused a message", message.data);
    return;
  }
  if (message.type === "pong") return;
  const keysFor = EVENT_MAP[message.type] as (data: ServerEvent["data"]) => QueryKey[] | undefined;
  if (!keysFor) {
    console.warn(`socket: no EVENT_MAP entry for ${message.type} — is the frontend older than the API?`);
    return;
  }
  keysFor(message.data)?.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
  listeners.forEach((listener) => listener(message));
}

/** Mount once, inside the signed-in app: keeps the socket open while mounted. */
export function useEvents(): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (process.env.REACT_APP_API_MOCK === "true") return; // MSW has no socket to answer
    return connectSocket((message) => handleMessage(message, queryClient));
  }, [queryClient]);
}

/** Run `onEvent` for one event type — for what a refetch can't do, like a toast. */
export function useEvent<T extends EventType>(type: T, onEvent: (data: DataOf<T>) => void): void {
  const latest = useRef(onEvent);
  latest.current = onEvent;
  useEffect(() => {
    const listener = (event: ServerEvent) => {
      if (event.type === type) latest.current(event.data as DataOf<T>);
    };
    listeners.add(listener);
    return () => void listeners.delete(listener);
  }, [type]);
}
